import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const inputClass =
  "w-full bg-[#141414] border border-white/15 px-5 py-4 text-sm text-[#F5F5F5] placeholder:text-[#5b5b63] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300";

export const InterestForm = () => {
  const [form, setForm] = useState({ name: "", email: "", role: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/interest/count");
        if (!cancelled) setCount(typeof data.count === "number" ? data.count : null);
      } catch {
        if (!cancelled) setCount(null);
      }
    };
    load();
    const timer = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/interest", {
        name: form.name,
        email: form.email,
        role: form.role || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Thank you — your interest has been recorded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="interest" className="relative py-28 lg:py-40" data-testid="interest-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} className="lg:col-span-5">
          <p className="text-[0.65rem] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Express Interest</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#F5F5F5]" data-testid="interest-heading">
            Be first to know when the <em className="italic text-[#D4AF37]">doors open.</em>
          </h2>
          <p className="mt-8 text-base leading-relaxed text-white" data-testid="interest-intro">
            Leave your details and we will contact you when the SHE Global™ Boardroom Accelerator
            program is announced. This is an expression of interest only — it is not an application,
            creates no obligation, and enrollment is not open.
          </p>
          <p className="mt-6 text-xs leading-relaxed text-[#5b5b63]">
            Your details are kept private, used solely to contact you about the Institute, and never
            published or shared.
          </p>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} className="lg:col-span-7">
          {done ? (
            <div className="border border-[#D4AF37]/50 bg-[#141414] p-12 lg:p-16 text-center" data-testid="interest-success-message">
              <span className="font-serif-display italic text-6xl text-[#D4AF37]">Thank you.</span>
              <p className="mt-6 text-base text-white max-w-md mx-auto leading-relaxed">
                Your interest in SHE Global™ Institute has been recorded. We will be in touch when
                program details are announced.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-[#0E0E0E] p-8 sm:p-12 space-y-6" data-testid="interest-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="interest-name" className="block text-[0.65rem] tracking-[0.3em] uppercase text-white mb-3">Full name *</label>
                  <input id="interest-name" data-testid="interest-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputClass} maxLength={120} />
                </div>
                <div>
                  <label htmlFor="interest-email" className="block text-[0.65rem] tracking-[0.3em] uppercase text-white mb-3">Email *</label>
                  <input id="interest-email" data-testid="interest-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputClass} />
                </div>
              </div>
              <div>
                <label htmlFor="interest-role" className="block text-[0.65rem] tracking-[0.3em] uppercase text-white mb-3">Current role <span className="normal-case tracking-normal text-[#5b5b63]">(optional)</span></label>
                <input id="interest-role" data-testid="interest-role-input" value={form.role} onChange={set("role")} placeholder="e.g. Senior Director, Founder" className={inputClass} maxLength={160} />
              </div>
              <div>
                <label htmlFor="interest-message" className="block text-[0.65rem] tracking-[0.3em] uppercase text-white mb-3">Anything you'd like us to know <span className="normal-case tracking-normal text-[#5b5b63]">(optional)</span></label>
                <textarea id="interest-message" data-testid="interest-message-input" value={form.message} onChange={set("message")} rows={4} placeholder="Where are you in your journey?" className={`${inputClass} resize-none`} maxLength={2000} />
              </div>
              {count !== null && count > 0 && (
                <p className="text-[0.7rem] tracking-[0.25em] uppercase text-[#D4AF37] text-center" data-testid="interest-count">
                  {count} {count === 1 ? "has" : "have"} joined the founding interest list
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="interest-submit-btn"
                className="w-full bg-[#D4AF37] text-black text-xs tracking-[0.3em] uppercase font-semibold px-10 py-5 hover:bg-[#C5A24D] disabled:opacity-60 transition-colors duration-300"
              >
                {submitting ? "Submitting…" : "Express Interest"}
              </button>
              <p className="text-[0.65rem] leading-relaxed text-[#5b5b63] text-center">
                Non-binding. No fees. No obligation. Enrollment is not open.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
